import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import StoneSummary from "./StoneSummary";
import DocStatusPill from "./DocStatusPill";

// Matches Diamond-PO-Prototype-v3.html's lineRow()/the stones <table class="dt"> in docDetail()
// (lines 954-967, 987-988), including the per-line actions cell (lines 957-959): "Report a
// discrepancy" (retailer) and "Clarified -- keep" / "Take it back" (supplier) -- Task 2188/F5.
// The row checkbox (Task 2182/F4) is likewise built: `selectable`/`selectedIds`/`onToggleSelect`
// and the action callbacks are all optional, so existing callers that don't pass them are
// unaffected (the Actions column is only rendered when at least one handler is provided).

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");
const dayDiff = (iso) => Math.max(1, Math.ceil((Date.now() - new Date(iso).getTime()) / 86400000));

export const DocLineTable = ({
  lines,
  docKind,
  isRetailer,
  shipmentsById,
  onOpenDoc,
  selectable,
  selectedIds,
  onToggleSelect,
  onReportDiscrepancy,
  onResolveDiscrepancy,
}) => {
  const { t } = useTranslation();
  const isMemo = docKind === "MEMO";
  const showActions = Boolean(onReportDiscrepancy || onResolveDiscrepancy);

  // Matches lineRow()'s own condition exactly: memo -- OUT; PO -- SHIPPED and its shipment
  // already delivered.
  const canReportDiscrepancy = (l) => {
    if (!isRetailer) return false;
    if (isMemo) return l.StatusCode === "OUT";
    const sh = l.OutShipmentID ? shipmentsById[l.OutShipmentID] : null;
    return l.StatusCode === "SHIPPED" && Boolean(sh && sh.DeliveredUtc);
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid #eee", color: "#6b7689", fontSize: 11, textTransform: "uppercase" }}>
            {selectable && <th style={{ padding: "8px 6px", width: 28 }} />}
            <th style={{ padding: "8px 6px" }}>{t("Stone")}</th>
            <th style={{ padding: "8px 6px" }}>{t("Status")}</th>
            <th style={{ padding: "8px 6px" }}>{t("Shipment")}</th>
            {isMemo && <th style={{ padding: "8px 6px" }}>{t("Due")}</th>}
            {isMemo && <th style={{ padding: "8px 6px", textAlign: "right" }}>{t("Base")}</th>}
            {isMemo && <th style={{ padding: "8px 6px", textAlign: "right" }}>{t("Memo margin")}</th>}
            <th style={{ padding: "8px 6px", textAlign: "right" }}>{isMemo ? t("Memo price") : t("Price")}</th>
            {showActions && <th style={{ padding: "8px 6px" }} />}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const snap = typeof l.StoneSnapshotJson === "string" ? JSON.parse(l.StoneSnapshotJson) : l.StoneSnapshotJson;
            const shipment = l.OutShipmentID ? shipmentsById[l.OutShipmentID] : null;
            const visible = !isRetailer || (shipment && shipment.ShowMarginToRetailer);
            const overdue = isMemo && l.DueUtc && ["OUT", "DISCREPANCY", "RETURN_REQUESTED"].includes(l.StatusCode) && new Date(l.DueUtc) < new Date();
            return (
              <tr key={l.DiamondLineID} style={{ borderBottom: "1px solid #f3f3f3" }}>
                {selectable && (
                  <td style={{ padding: "8px 6px" }}>
                    {selectable(l) && (
                      <input
                        type="checkbox"
                        checked={Boolean(selectedIds && selectedIds.includes(l.DiamondLineID))}
                        onChange={(e) => onToggleSelect && onToggleSelect(l.DiamondLineID, e.target.checked)}
                      />
                    )}
                  </td>
                )}
                <td style={{ padding: "8px 6px" }}>
                  <StoneSummary snap={snap} note={l.DiscrepancyNote} />
                </td>
                <td style={{ padding: "8px 6px" }}>
                  <DocStatusPill status={l.StatusCode} />
                  {l.PurchasedOnDocID && onOpenDoc && (
                    <div style={{ marginTop: 3 }}>
                      <span style={{ color: "#3b5bdb", cursor: "pointer", fontSize: 12 }} onClick={() => onOpenDoc(l.PurchasedOnDocID)}>
                        {t("View purchase order")}
                      </span>
                    </div>
                  )}
                </td>
                <td style={{ padding: "8px 6px", color: "#79838f" }}>
                  {shipment ? (
                    <div>
                      {t("Shipped")}
                      <div style={{ fontSize: 11 }}>{fmtDate(shipment.ShippedUtc)}</div>
                    </div>
                  ) : (
                    t("not shipped")
                  )}
                </td>
                {isMemo && (
                  <td style={{ padding: "8px 6px" }}>
                    {l.DueUtc && ["OUT", "DISCREPANCY", "RETURN_REQUESTED"].includes(l.StatusCode) ? (
                      overdue ? (
                        <span style={{ color: "#c0392b", fontWeight: 600 }}>
                          {dayDiff(l.DueUtc)}
                          {t("d overdue")}
                        </span>
                      ) : (
                        fmtDate(l.DueUtc)
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                )}
                {isMemo && <td style={{ padding: "8px 6px", textAlign: "right" }}>{visible ? fmtMoney(l.BaseAmount) : "—"}</td>}
                {isMemo && (
                  <td style={{ padding: "8px 6px", textAlign: "right" }}>
                    {visible ? (l.MarginAmount ? "+" + fmtMoney(l.MarginAmount) : "—") : "—"}
                  </td>
                )}
                <td style={{ padding: "8px 6px", textAlign: "right", fontWeight: 700 }}>{fmtMoney(l.LineTotal)}</td>
                {showActions && (
                  <td style={{ padding: "8px 6px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {onReportDiscrepancy && canReportDiscrepancy(l) && (
                      <Button size="small" type="link" onClick={() => onReportDiscrepancy(l.DiamondLineID)}>
                        {t("Report a discrepancy")}
                      </Button>
                    )}
                    {onResolveDiscrepancy && !isRetailer && l.StatusCode === "DISCREPANCY" && (
                      <>
                        <Button size="small" onClick={() => onResolveDiscrepancy(l.DiamondLineID, "CLARIFY")}>
                          {t("Clarified — keep")}
                        </Button>{" "}
                        <Button size="small" danger onClick={() => onResolveDiscrepancy(l.DiamondLineID, "TAKEBACK")}>
                          {t("Take it back")}
                        </Button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DocLineTable;
